import { useContext, useEffect, useMemo, useState } from "react"
import { XmppContext } from "@/stream/xmppClient"
import AdHocCommands from "@/api/adHocCommands"
import { parseAlertDetail, parseTelemetry } from "./fuelReportParser"
import { parseUtcIso } from "./parseUtcIso"
import type { AlertDetail, TelemetryPoint } from "./fuelReportTypes"
import i18n from "@/lib/i18n/i18n"

// lookaheadHours must survive the mandatory 9-11h overnight rest AND a
// weekend pause (Friday alert -> Monday-morning refuel) — keep in sync with
// gps ALERT_LOOKAHEAD_HOURS.
const DEFAULT_OPTS = { lookaheadHours: 72, radiusM: 500, seenHorizonHours: 24 }
const SEEN_PAD_MS = 30 * 60 * 1000
// gps get_fuel_telemetry rejects ranges longer than 1 day — never let the seen
// extension turn a working window into an error.
const MAX_TELEMETRY_WINDOW_MS = 24 * 60 * 60 * 1000

/**
 * Widen the telemetry window to include the "seen" time (±30 min) when the
 * driver saw the alert outside the base [alert, refuel] range — so the playback
 * track reaches the eye marker. Returns the base window unchanged otherwise.
 *
 * The extension is clamped to the gps 1-day telemetry cap: the base window is
 * kept intact and the seen side gives up whatever exceeds the cap (the eye
 * marker may then sit past the track's end, but telemetry still loads).
 */
export function extendWindowForSeen(
  window: { from: string; to: string },
  seenAt: string | null | undefined,
): { from: string; to: string } {
  if (!seenAt) return window
  const seen = parseUtcIso(seenAt).getTime()
  const from = parseUtcIso(window.from).getTime()
  const to = parseUtcIso(window.to).getTime()
  if (Number.isNaN(seen) || Number.isNaN(from) || Number.isNaN(to))
    return window
  let newFrom = Math.min(from, seen - SEEN_PAD_MS)
  let newTo = Math.max(to, seen + SEEN_PAD_MS)
  if (newTo - newFrom > MAX_TELEMETRY_WINDOW_MS) {
    if (seen > to) newTo = newFrom + MAX_TELEMETRY_WINDOW_MS
    else newFrom = newTo - MAX_TELEMETRY_WINDOW_MS
  }
  if (newFrom === from && newTo === to) return window
  return {
    from: new Date(newFrom).toISOString(),
    to: new Date(newTo).toISOString(),
  }
}

export function useFuelAlertDetail(
  driverJid: string,
  eventId: string,
  window: { from: string; to: string },
  opts: {
    lookaheadHours: number
    radiusM: number
    seenHorizonHours: number
  } = DEFAULT_OPTS,
) {
  const { client } = useContext(XmppContext)
  const [detail, setDetail] = useState<AlertDetail | null>(null)
  const [telemetry, setTelemetry] = useState<TelemetryPoint[]>([])
  const [detailLoading, setDetailLoading] = useState(true)
  const [telemetryLoading, setTelemetryLoading] = useState(true)
  // Scoped errors: a telemetry failure must not poison the whole dialog when
  // the detail (verdict/stations/seen) loaded fine — and vice versa.
  const [error, setError] = useState<string | null>(null)
  const [telemetryError, setTelemetryError] = useState<string | null>(null)

  // Detail (verdict, stations, driver pos, seen) — independent of the telemetry
  // window, so extending that window for the seen time never re-runs the
  // (archive-scanning) detail query.
  useEffect(() => {
    let cancelled = false
    if (!client) return
    setDetailLoading(true)
    AdHocCommands.getFuelAlertDetail(client, driverJid, eventId, opts)
      .then(parseAlertDetail)
      .then((d) => {
        if (!cancelled) setDetail(d)
      })
      .catch((err) => {
        if (!cancelled) {
          setError(
            err instanceof Error
              ? err.message
              : i18n.t("reports.fuelDetail.loadFailed"),
          )
        }
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false)
      })
    return () => {
      cancelled = true
    }
    // Keyed on the opts primitives (not the object ref) so an inline opts object
    // never triggers a refetch loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    client,
    driverJid,
    eventId,
    opts.lookaheadHours,
    opts.radiusM,
    opts.seenHorizonHours,
  ])

  // Telemetry — window extended to include the seen time once it's known.
  // Keyed on the window bounds (not the object ref) for the same reason.
  const eff = useMemo(
    () => extendWindowForSeen(window, detail?.seen?.seenAt),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [window.from, window.to, detail?.seen?.seenAt],
  )
  useEffect(() => {
    let cancelled = false
    if (!client) return
    setTelemetryLoading(true)
    setTelemetryError(null)
    AdHocCommands.getFuelTelemetry(client, driverJid, eff.from, eff.to)
      .then(parseTelemetry)
      .then((t) => {
        if (!cancelled) setTelemetry(t)
      })
      .catch((err) => {
        if (!cancelled) {
          setTelemetryError(
            err instanceof Error
              ? err.message
              : i18n.t("reports.fuelDetail.telemetryLoadFailed"),
          )
        }
      })
      .finally(() => {
        if (!cancelled) setTelemetryLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [client, driverJid, eff.from, eff.to])

  return {
    detail,
    telemetry,
    isLoading: detailLoading || telemetryLoading,
    error,
    telemetryError,
  }
}
